<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, toRaw, watch } from "vue"
import { useForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import type { Services } from "@services"
import type { PvEtudeEcoFullForm } from "@modules/pv/types/pv-etude-eco-form.types"
import {
    buildPvEtudeEcoCreatePayload,
    createDefaultPvEtudeEcoForm,
} from "@modules/pv/utils/pv-etude-eco-form"
import {
    validatePvEtudeEcoFormWithYup,
    type PvEtudeEcoFormSchemaMessages,
} from "@modules/pv/utils/pv-etude-eco-form-schema"
import PVEtudeEcoFormGeneralInfo from "./PVEtudeEcoFormGeneralInfo.vue"
import PVEtudeEcoFormSubscribe from "./PVEtudeEcoFormSubscribe.vue"
import PVEtudeEcoFormPrice from "./PVEtudeEcoFormPrice.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Créer une étude économique",
            generalNameTitle: "Informations générales",
            nameLabel: "Nom de l'étude",
            namePlaceholder: "Étude économique n°1",
            quit: "Quitter",
            submit: "Valider et lancer l'étude",
            errorForm:
                "Des champs sont invalides ou bien manquants. Veuillez les corriger avant de poursuivre.",
            errorNameEmpty: "Veuillez saisir le nom de l'étude.",
            errorNameInvalid:
                "Seuls les caractères alphanumériques, le point, le trait d'union et le trait de soulignement sont autorisés",
            errorSubmit: "La création de l’étude a échoué.",
            errorNumericRequired:
                "Ce champ est obligatoire et doit être un nombre valide.",
            errorDecimals:
                "Ce nombre ne peut pas dépasser 4 décimales pour les champs de type prix.",
            errorPlageStart: "Indiquez l’heure de début de la plage.",
            errorPlageEnd: "Indiquez l’heure de fin de la plage.",
            errorPlageBlock: "Les plages horaires sont incomplètes.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Identifiant Mongo de l’analyse de dimensionnement PV (_id dimensionnement). */
        dimensionnementId: string
        /** Segment tarifaire du PDL (ex. C5) — impacte les options de tarification. */
        segment?: string
    }>(),
    {
        segment: "C5",
    }
)

const emit = defineEmits<{
    quit: []
    done: [analyse: Record<string, unknown>]
}>()

// ================================
// COMPOSABLES — services
// ================================
const services = inject<Services>("services")
if (!services) {
    throw new Error("PVEtudeEcoForm: services non injectés")
}
const { $createEtudeEco } = services

// ================================
// FORMULAIRE (valeurs vee-validate sans schéma intégré — évite la validation silencieuse au montage)
// ================================
const { errors, resetForm, setErrors, defineField } =
    useForm<PvEtudeEcoFullForm>({
        initialValues: createDefaultPvEtudeEcoForm(),
        validateOnMount: false,
    })

/**
 * Champs racine liés au store interne vee-validate (évite `readonly(values)` qui casse les mutations profondes).
 */
const [nameAnalyse] = defineField("name_analyse")
const [generalInfo] = defineField("general_info")
const [subscribeBefore] = defineField("subscribe_before")
const [subscribeAfter] = defineField("subscribe_after")
const [price] = defineField("price")

/**
 * Copie métier du formulaire pour Yup / API (hors proxies Vue).
 */
function getFormSnapshot(): PvEtudeEcoFullForm {
    return structuredClone({
        name_analyse: nameAnalyse.value,
        general_info: toRaw(generalInfo.value),
        subscribe_before: toRaw(subscribeBefore.value),
        subscribe_after: toRaw(subscribeAfter.value),
        price: toRaw(price.value),
    }) as PvEtudeEcoFullForm
}

/** Source réactive pour re-valider après un submit refusé (mutations profondes incluses). */
const getWatchedFormState = computed(() => ({
    name_analyse: nameAnalyse.value,
    general_info: generalInfo.value,
    subscribe_before: subscribeBefore.value,
    subscribe_after: subscribeAfter.value,
    price: price.value,
}))

const getSchemaMessages = computed(
    (): PvEtudeEcoFormSchemaMessages => ({
        nameRequired: t("errorNameEmpty"),
        nameInvalid: t("errorNameInvalid"),
        numericRequired: t("errorNumericRequired"),
        decimalsInvalid: t("errorDecimals"),
        plageStartRequired: t("errorPlageStart"),
        plageEndRequired: t("errorPlageEnd"),
        plageBlockInvalid: t("errorPlageBlock"),
    })
)

/** Chemins ayant encore une erreur (pour les effacer avant d’appliquer le prochain lot). */
const lastErrorPaths = ref<string[]>([])

/** Après un submit refusé, on re-valide à chaque changement profond des valeurs. */
const didAttemptSubmit = ref(false)

/**
 * Efface les erreurs vee-validate sur les chemins précédemment signalés.
 */
function clearReportedErrors(): void {
    if (!lastErrorPaths.value.length) {
        return
    }
    const cleared: Record<string, string | undefined> = {}
    for (const p of lastErrorPaths.value) {
        cleared[p] = undefined
    }
    setErrors(cleared)
    lastErrorPaths.value = []
}

/**
 * Valide avec Yup et met à jour `errors` via `setErrors`.
 * @returns true si le formulaire est valide
 */
async function syncYupErrorsToForm(): Promise<boolean> {
    const snapshot = getFormSnapshot()
    const result = await validatePvEtudeEcoFormWithYup(
        snapshot,
        getSchemaMessages.value
    )
    clearReportedErrors()
    if (result.ok) {
        return true
    }
    setErrors(result.errors)
    lastErrorPaths.value = Object.keys(result.errors)
    return false
}

/**
 * Erreurs vee-validate pour une section, clés relatives au préfixe (ex. `prix_installation`).
 */
function pickFieldErrorsByPrefix(
    prefix: string
): Record<string, string | undefined> {
    const p = `${prefix}.`
    const out: Record<string, string | undefined> = {}
    for (const [key, val] of Object.entries(errors.value)) {
        if (val == null || val === "") continue
        const msg = Array.isArray(val) ? val[0] : val
        if (key.startsWith(p)) {
            out[key.slice(p.length)] = msg
        }
    }
    return out
}

const getGeneralInfoFieldErrors = computed(() =>
    pickFieldErrorsByPrefix("general_info")
)
const getPriceFieldErrors = computed(() => pickFieldErrorsByPrefix("price"))
const getSubscribeBeforeFieldErrors = computed(() =>
    pickFieldErrorsByPrefix("subscribe_before")
)
const getSubscribeAfterFieldErrors = computed(() =>
    pickFieldErrorsByPrefix("subscribe_after")
)

// ================================
// ÉTAT
// ================================
const isSubmitting = ref(false)
const displayMessage = ref(false)
const messageSeverity = ref<"error" | "success">("error")
const messageText = ref("")

// ================================
// WATCHERS
// ================================
watch(
    () => Object.keys(errors.value).length,
    (n) => {
        if (n === 0) displayMessage.value = false
    }
)

watch(
    getWatchedFormState,
    async () => {
        if (!didAttemptSubmit.value || isSubmitting.value) {
            return
        }
        await syncYupErrorsToForm()
    },
    { deep: true }
)

// ================================
// INTERACTIONS
// ================================
function onQuit(): void {
    didAttemptSubmit.value = false
    clearReportedErrors()
    resetForm({ values: createDefaultPvEtudeEcoForm() })
    displayMessage.value = false
    emit("quit")
}

function showError(text: string): void {
    messageText.value = text
    messageSeverity.value = "error"
    displayMessage.value = true
}

function onInvalidSubmit(): void {
    showError(t("errorForm"))
}

async function onSubmit(): Promise<void> {
    didAttemptSubmit.value = true
    const ok = await syncYupErrorsToForm()
    if (!ok) {
        onInvalidSubmit()
        return
    }

    isSubmitting.value = true
    let created: Record<string, unknown> | null = null
    const formValues = getFormSnapshot()
    try {
        const payload = buildPvEtudeEcoCreatePayload(formValues)
        created = await $createEtudeEco(
            props.dimensionnementId,
            formValues.name_analyse.trim(),
            payload
        )
    } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : t("errorSubmit")
        showError(msg)
    } finally {
        isSubmitting.value = false
        didAttemptSubmit.value = false
        clearReportedErrors()
        resetForm({ values: createDefaultPvEtudeEcoForm() })
        if (created) {
            emit("done", created)
        }
    }
}
</script>

<template>
    <div class="pv-etude-eco-form flex flex-col gap-8 max-w-4xl mx-auto w-full">
        <h2 class="text-lg font-semibold text-primary-900 text-center">
            {{ t("title") }}
        </h2>

        <div class="flex flex-col gap-3">
            <h3 class="text-base font-semibold text-primary-900">
                {{ t("generalNameTitle") }}
            </h3>
            <BaseInputWrapper
                input-id="pv-eco-name-analyse"
                :label="t('nameLabel')"
                :error="errors.name_analyse"
            >
                <BaseInputText
                    id="pv-eco-name-analyse"
                    v-model="nameAnalyse"
                    class="w-full"
                    :placeholder="t('namePlaceholder')"
                    :invalid="Boolean(errors.name_analyse)"
                />
            </BaseInputWrapper>
        </div>

        <PVEtudeEcoFormGeneralInfo
            v-model="generalInfo"
            :field-errors="getGeneralInfoFieldErrors"
        />

        <PVEtudeEcoFormSubscribe
            v-model="subscribeBefore"
            :segment="segment"
            :field-errors="getSubscribeBeforeFieldErrors"
            before
        />
        <PVEtudeEcoFormSubscribe
            v-model="subscribeAfter"
            :segment="segment"
            :field-errors="getSubscribeAfterFieldErrors"
            :before="false"
        />

        <PVEtudeEcoFormPrice
            v-model="price"
            :field-errors="getPriceFieldErrors"
        />

        <BaseMessage
            v-if="displayMessage"
            :severity="messageSeverity"
            :closable="false"
        >
            {{ messageText }}
        </BaseMessage>

        <div
            class="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-2"
        >
            <BaseButton
                :label="t('quit')"
                variant="outlined"
                severity="secondary"
                class="w-full sm:w-auto"
                :disabled="isSubmitting"
                @click="onQuit"
            />
            <BaseButton
                :label="t('submit')"
                class="w-full sm:w-auto"
                :loading="isSubmitting"
                :disabled="isSubmitting"
                @click="onSubmit"
            />
        </div>
    </div>
</template>
